import React from "react";
import { gradeColors } from "../sustainabilityIndex/gradeColors";
import { asDisplayLabel } from "../../utils/benchmarking/benchmarkEngine";

function formatScore(value) {
  if (value == null || !Number.isFinite(value)) return "—";
  return Number(value).toFixed(1);
}

function formatMetaPart(value) {
  return asDisplayLabel(value, "");
}

function formatRank(rank, cohort) {
  if (rank == null) return "—";
  return cohort ? `#${rank.toLocaleString()} of ${cohort.toLocaleString()}` : `#${rank.toLocaleString()}`;
}

function MetricCard({ label, value, subtext, accent, large }) {
  return (
    <div className={`benchmark-dashboard-metric${large ? " benchmark-dashboard-metric--large" : ""}`}>
      <span className="benchmark-dashboard-metric__label">{label}</span>
      <span
        className="benchmark-dashboard-metric__value"
        style={accent ? { color: accent } : undefined}
      >
        {value}
      </span>
      {subtext ? <span className="benchmark-dashboard-metric__sub">{subtext}</span> : null}
    </div>
  );
}

export default function BenchmarkDashboard({ dashboard, benchmarkLabel, orgName }) {
  if (!dashboard) return null;

  const grade = String(dashboard.esgRatingGrade || "—").trim().charAt(0);
  const gradeColor = gradeColors[grade] || "#02b880";
  const percentile = dashboard.percentileRank;

  return (
    <section className="benchmark-dashboard" aria-label="Benchmark dashboard">
      <div className="benchmark-dashboard__hero">
        <div className="benchmark-dashboard__hero-main">
          <p className="benchmark-dashboard__eyebrow">Benchmark Dashboard</p>
          <h2 className="benchmark-dashboard__title">
            {orgName || "Your organization"}
          </h2>
          <p className="benchmark-dashboard__subtitle">
            Compared against <strong>{formatMetaPart(benchmarkLabel) || "benchmark"}</strong>
            {formatMetaPart(dashboard.sector)
              ? ` · ${formatMetaPart(dashboard.sector)}`
              : ""}
            {formatMetaPart(dashboard.country)
              ? ` · ${formatMetaPart(dashboard.country)}`
              : ""}
          </p>
        </div>
        <div className="benchmark-dashboard__hero-grade" style={{ borderColor: gradeColor }}>
          <span className="benchmark-dashboard__hero-grade-label">ESG Rating Grade</span>
          <span className="benchmark-dashboard__hero-grade-value" style={{ color: gradeColor }}>
            {grade}
          </span>
        </div>
      </div>

      <div className="benchmark-dashboard__grid">
        <MetricCard
          label="Overall Sustainability Score"
          value={formatScore(dashboard.overallSustainabilityScore)}
          subtext="Your SI composite score (0–100)"
          large
        />
        <MetricCard
          label="Overall Benchmark Score"
          value={formatScore(dashboard.overallBenchmarkScore)}
          subtext={formatMetaPart(benchmarkLabel) || undefined}
          large
        />
        <MetricCard
          label="Percentile Rank"
          value={percentile != null ? `${percentile}th` : "—"}
          subtext="Higher is better vs peer universe"
          accent={percentile >= 75 ? "#059669" : percentile >= 50 ? "#d97706" : "#dc2626"}
        />
        <MetricCard
          label="Organization Rank"
          value={formatRank(dashboard.organizationRank, dashboard.organizationCohort)}
          subtext="Within size & sector peer group"
        />
        <MetricCard
          label="Industry Rank"
          value={formatRank(dashboard.industryRank, dashboard.industryCohort)}
          subtext={
            formatMetaPart(dashboard.sector)
              ? `${formatMetaPart(dashboard.sector)} sector`
              : "Industry peers"
          }
        />
        <MetricCard
          label="Global Rank"
          value={formatRank(dashboard.globalRank, dashboard.globalCohort)}
          subtext="GE3S global sustainability index universe"
        />
        <MetricCard
          label="ESG Rating Grade"
          value={grade}
          subtext="Letter grade from assessment"
          accent={gradeColor}
        />
        <MetricCard
          label="Assessment Year"
          value={dashboard.assessmentYear ?? "—"}
          subtext="Reporting period for this benchmark"
        />
      </div>

      {percentile != null && (
        <div className="benchmark-dashboard__percentile-bar">
          <div className="benchmark-dashboard__percentile-track">
            <div
              className="benchmark-dashboard__percentile-fill"
              style={{ width: `${percentile}%` }}
            />
            <div
              className="benchmark-dashboard__percentile-marker"
              style={{ left: `${percentile}%` }}
              title={`${percentile}th percentile`}
            />
          </div>
          <div className="benchmark-dashboard__percentile-labels">
            <span>0th</span>
            <span>50th</span>
            <span>99th percentile</span>
          </div>
        </div>
      )}
    </section>
  );
}
